import { useState, useMemo, useEffect } from 'react'
import type { PlanningPeriodKey } from '@crm/shared'
import { analyticsUi } from './analyticsUiState'
import { useAuthStore } from '../auth/authStore'
import { PlanningPeriodSelector } from '../planning/PlanningPeriodSelector'
import { SummaryTab } from './summary/SummaryTab'
import { KpTimelineTab } from './KpTimelineTab'
import { OverviewTab } from './OverviewTab'
import { ManagersTab } from './ManagersTab'
import { ClientsTab } from './ClientsTab'
import { BuyersTab } from './BuyersTab'
import { SuppliersTab } from './SuppliersTab'
import { CustomsSendsTab } from './CustomsSendsTab'
import { DigestBanner } from './DigestBanner'
import type { AnalyticsPeriodParams } from '../../shared/api/analytics.api'

/**
 * Модуль «Аналитика» — каркас (этап 1 по docs/analytics-plan.md).
 *
 * Пункт меню, 5 вкладок, селектор периода, доступы по ролям. Вкладки пока
 * показывают плановый состав виджетов — наполнение идёт следующими этапами.
 *
 * Доступ: ADMIN — все вкладки; ROP — продажи (Обзор/Клиенты/Менеджеры);
 * ROS — закупки (Закупщики/Поставщики).
 */

interface AnalyticsTab {
  key: string
  label: string
  roles: string[]
  /** Плановый состав виджетов вкладки — показывается до наполнения. */
  widgets: string[]
}

const TABS: AnalyticsTab[] = [
  {
    key: 'summary',
    label: 'Сводка',
    roles: ['ADMIN', 'GENERAL_DIRECTOR', 'ROP'],
    widgets: [
      'KPI за период с дельтой к предыдущему',
      'Воронка КП→заказ→отказ→в работе + исходы',
      'Топ сегментов: объём и в заказ + конверсия направлений',
      'Матрицы и бренды',
      'Клиенты и менеджеры периода',
      'Сравнение периодов (Май vs Июнь) с фильтрами',
    ],
  },
  {
    key: 'kp',
    label: 'КП',
    roles: ['ADMIN', 'GENERAL_DIRECTOR', 'ROP'],
    widgets: [
      'Таймлайн заявок по менеджерам: создана → проквотирована → КП → отправлено',
      'Следующее действие из блока «Контроль КП» + последнее выполненное касание',
      'Статус заявки и общий счётчик времени до финала',
      'Провалиться в заявку → экран истории заявки',
    ],
  },
  {
    key: 'overview',
    label: 'Обзор',
    roles: ['ADMIN', 'GENERAL_DIRECTOR', 'ROP'],
    widgets: [
      'Сквозная воронка проекта (8 стадий) с конверсией и временем',
      'Тепловая карта узких мест',
      '«Где сейчас застряло» — позиции дольше порога на этапе',
      'Срыв дедлайнов с разбивкой вины (продажи / закупка)',
      'Динамика по дням',
    ],
  },
  {
    key: 'clients',
    label: 'Клиенты',
    roles: ['ADMIN', 'GENERAL_DIRECTOR', 'ROP'],
    widgets: [
      'Конверсия по сегменту и категории',
      'Карта «объём vs выхлоп» (scatter)',
      'Топ / Антитоп клиентов',
      'Расхождение «категория vs поведение»',
      '«Спящие» клиенты',
    ],
  },
  {
    key: 'managers',
    label: 'Менеджеры',
    roles: ['ADMIN', 'GENERAL_DIRECTOR', 'ROP'],
    widgets: [
      'План/факт: финансы + АКБ в одной таблице',
      'Активная клиентская база',
      'Воронка менеджера',
      'Скорость работы и нагрузка',
      'Срывы дедлайнов и качество заявок',
    ],
  },
  {
    key: 'customs',
    label: 'Нетарифка',
    roles: ['ADMIN', 'GENERAL_DIRECTOR', 'ROP'],
    widgets: ['Какие позиции из-за рубежа менеджеры отправляли логисту, кто и когда'],
  },
  {
    key: 'buyers',
    label: 'Закупщики',
    roles: ['ADMIN', 'GENERAL_DIRECTOR', 'ROS'],
    widgets: [
      'Воронка закупщика',
      'Скорость ответа на заявки',
      'Объём квот и запросов поставщикам',
      'Эффективность («выхлоп») квот',
      'Завершённость заявок, широта базы поставщиков',
    ],
  },
  {
    key: 'suppliers',
    label: 'Поставщики',
    roles: ['ADMIN', 'GENERAL_DIRECTOR', 'ROS'],
    widgets: [
      'Реестр-воронка: запрошено → ответил → дал цену → в заказ',
      'Квадрант «отклик vs выигрыш»',
      'Скорость ответа поставщика',
      'Ценовая конкурентность, ABC поставщиков',
      'Карточка поставщика (drill-down)',
    ],
  },
]

export function AnalyticsPage() {
  const { user } = useAuthStore()
  const role = user?.role ?? ''

  const visibleTabs = useMemo(() => TABS.filter((t) => t.roles.includes(role)), [role])

  // Восстанавливаем последнюю вкладку/период из стора — чтобы возврат из заявки
  // не сбрасывал на «Сводку» и не терял выбранный период.
  const [activeKey, setActiveKey] = useState<string>(() => {
    const saved = analyticsUi.getTab()
    if (saved && visibleTabs.some((t) => t.key === saved)) return saved
    return visibleTabs[0]?.key ?? 'overview'
  })
  const savedPeriod = analyticsUi.getPeriod()
  const [periodKey, setPeriodKey] = useState<PlanningPeriodKey>(
    (savedPeriod?.periodKey as PlanningPeriodKey) ?? 'month',
  )
  const [date, setDate] = useState<string>(
    () => savedPeriod?.date ?? new Date().toISOString().slice(0, 7),
  )
  const [from, setFrom] = useState<string | null>(savedPeriod?.from ?? null)
  const [to, setTo] = useState<string | null>(savedPeriod?.to ?? null)

  // Сохраняем вкладку и период в стор (переживают уход со страницы и возврат).
  useEffect(() => {
    analyticsUi.setTab(activeKey)
  }, [activeKey])
  useEffect(() => {
    analyticsUi.setPeriod({ periodKey, date, from, to })
  }, [periodKey, date, from, to])

  if (visibleTabs.length === 0) {
    return (
      <div className="space-y-4">
        <h1 className="text-xl font-semibold text-gray-900">Аналитика</h1>
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-500">
          Раздел доступен только администратору и руководителям отделов.
        </div>
      </div>
    )
  }

  const activeTab = visibleTabs.find((t) => t.key === activeKey) ?? visibleTabs[0]!

  // Параметры периода для analytics-эндпоинтов (бэкенд парсит как в /planning).
  const periodParams: AnalyticsPeriodParams = {
    period: periodKey,
    date: periodKey === 'custom' ? null : date,
    from: periodKey === 'custom' ? from : null,
    to: periodKey === 'custom' ? to : null,
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-xl font-semibold text-gray-900">Аналитика</h1>
        <PlanningPeriodSelector
          periodKey={periodKey}
          date={date}
          from={from}
          to={to}
          onPeriodChange={setPeriodKey}
          onDateChange={setDate}
          onCustomChange={(f, t) => {
            setFrom(f)
            setTo(t)
          }}
        />
      </div>

      {/* Сводный «светофор» — этап 6 */}
      <DigestBanner periodParams={periodParams} />

      {/* Вкладки */}
      <div className="flex items-center gap-1 flex-wrap border-b border-gray-200">
        {visibleTabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveKey(tab.key)}
            className={`px-3 py-2 text-sm -mb-px border-b-2 transition-colors ${
              tab.key === activeTab.key
                ? 'border-brand-600 text-brand-700 font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Содержимое вкладки. Наполнены: «Сводка», «КП», «Обзор», «Менеджеры», «Клиенты». */}
      {activeTab.key === 'summary' ? (
        <SummaryTab periodParams={periodParams} />
      ) : activeTab.key === 'kp' ? (
        <KpTimelineTab periodParams={periodParams} />
      ) : activeTab.key === 'overview' ? (
        <OverviewTab periodParams={periodParams} />
      ) : activeTab.key === 'managers' ? (
        <ManagersTab periodParams={periodParams} />
      ) : activeTab.key === 'clients' ? (
        <ClientsTab periodParams={periodParams} />
      ) : activeTab.key === 'customs' ? (
        <CustomsSendsTab periodParams={periodParams} />
      ) : activeTab.key === 'buyers' ? (
        <BuyersTab periodParams={periodParams} />
      ) : activeTab.key === 'suppliers' ? (
        <SuppliersTab periodParams={periodParams} />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-gray-900">{activeTab.label}</h2>
            <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs">
              в разработке
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Раздел будет наполнен следующими этапами. Плановый состав виджетов:
          </p>
          <ul className="mt-3 space-y-1.5">
            {activeTab.widgets.map((w) => (
              <li key={w} className="flex items-start gap-2 text-sm text-gray-700">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-gray-300 flex-shrink-0" />
                {w}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
